// Automatic import of past invoices.
//
// Every source (text PDF, scanned PDF / photo via OCR, Excel, CSV) is turned into
// the same thing — rows of positioned text cells — and read by one parser that
// finds the item table by its column headings (Description / Qty / Rate / Tax /
// Amount … in any order or wording), the bill-to block, the invoice fields and
// the totals. Anything the file doesn't contain is left blank. Spreadsheets that
// are registers (one row per invoice or per line) are detected and grouped
// automatically. No manual column mapping is needed.

import { CURRENCIES, num, round2 } from "./utils";
import { basePath } from "./geo";

const MONTHS = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, sept: 9, oct: 10, nov: 11, dec: 12 };
const CODES = Object.keys(CURRENCIES);
const SYMBOLS = [
  ["GBP", /£/g],
  ["INR", /₹|\bRs\.?\s?\d/g],
  ["EUR", /€/g],
  ["AUD", /A\$/g],
  ["CAD", /C\$/g],
  ["SGD", /S\$/g],
  ["NZD", /NZ\$/g],
  ["HKD", /HK\$/g],
  ["JPY", /¥/g],
  ["PHP", /₱/g],
  ["NGN", /₦/g],
  ["TRY", /₺/g],
  ["KRW", /₩/g],
  ["THB", /฿/g],
];
const CUR_TOKEN = new RegExp(`^(?:${["£", "₹", "\\$", "€", "¥", "₱", "₦", "₺", "₩", "฿", "Rs\\.?", "A\\$", "C\\$", "S\\$", "NZ\\$", "HK\\$", "US\\$", "R\\$", "MX\\$", "RM", "Rp", "KSh", ...CODES].join("|")})$`, "i");

// ---------------- numbers & dates ----------------

// Parse an amount such as "£1,234.50", "1.234,50 €", "(120.00)", "INR 5,000", "12%".
// Returns null when the text isn't a number.
export function parseAmount(s) {
  if (s == null) return null;
  if (typeof s === "number") return Number.isFinite(s) ? s : null;
  let t = String(s).trim();
  if (!t || t.length > 40) return null;
  let neg = /^\(.*\)$/.test(t) || /^-|^−|-$|\bCR$/i.test(t);
  t = t.replace(/^\((.*)\)$/, "$1").replace(/\b(CR|DR)$/i, "");
  t = t.replace(new RegExp(`(${CODES.join("|")}|Rs\\.?|RM|Rp|KSh|US)`, "gi"), "");
  t = t.replace(/[£₹$€¥₱₦₺₩฿%\s'’]/g, "").replace(/^[-−]|[-−]$/g, "");
  if (/^[-−]/.test(t)) neg = true;
  if (!/^\d[\d.,]*$/.test(t)) return null;
  if (/^\d{1,3}(\.\d{3})+(,\d{1,2})?$/.test(t) || /^\d+,\d{1,2}$/.test(t)) t = t.replace(/\./g, "").replace(",", "."); // 1.234,50
  else t = t.replace(/,/g, "");
  if (!/^\d+(\.\d+)?$/.test(t)) return null;
  const n = parseFloat(t);
  return Number.isFinite(n) ? (neg ? -n : n) : null;
}
// A cell that is clearly money (has decimals, thousands separators or a currency sign).
export const isMoney = (s) => {
  const t = String(s ?? "").trim();
  return parseAmount(t) != null && (/\d[.,]\d{2}$/.test(t.replace(/\s*(CR|DR|[A-Z]{3})$/i, "")) || /\d,\d{3}/.test(t) || /[£₹$€¥₱₦₺₩฿]|Rs\.?/.test(t));
};
export const money = (s) => parseAmount(s) ?? num(s);

// dd/mm vs mm/dd is decided per value when possible, otherwise by `order`.
export function parseDate(s, order = "dmy") {
  if (s == null || s === "") return "";
  if (s instanceof Date) return Number.isNaN(s.getTime()) ? "" : s.toISOString().slice(0, 10);
  if (typeof s === "number" || /^\d{5}(\.\d+)?$/.test(String(s).trim())) {
    const n = Number(s);
    if (n > 20000 && n < 80000) return new Date(Date.UTC(1899, 11, 30) + Math.floor(n) * 864e5).toISOString().slice(0, 10); // Excel serial
    return "";
  }
  s = String(s).trim();
  let m;
  const mon = (w) => MONTHS[String(w).slice(0, 4).toLowerCase()] || MONTHS[String(w).slice(0, 3).toLowerCase()];
  if ((m = s.match(/(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/))) return iso(m[1], m[2], m[3]);
  if ((m = s.match(/(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})\b/))) {
    let [a, b] = [Number(m[1]), Number(m[2])];
    const y = m[3].length === 2 ? "20" + m[3] : m[3];
    if (a > 12 && b <= 12) return iso(y, b, a);
    if (b > 12 && a <= 12) return iso(y, a, b);
    return order === "mdy" ? iso(y, a, b) : iso(y, b, a);
  }
  if ((m = s.match(/(\d{1,2})(?:st|nd|rd|th)?[\s\-.]+(?:of\s+)?([A-Za-z]{3,9})\.?,?[\s\-.]+(\d{2,4})/)) && mon(m[2])) return iso(m[3].length === 2 ? "20" + m[3] : m[3], mon(m[2]), m[1]);
  if ((m = s.match(/([A-Za-z]{3,9})\.?[\s\-.]+(\d{1,2})(?:st|nd|rd|th)?,?[\s\-.]+(\d{4})/)) && mon(m[1])) return iso(m[3], mon(m[1]), m[2]);
  return "";
}
function iso(y, m, d) {
  y = Number(y);
  m = Number(m);
  d = Number(d);
  if (!(y > 1990 && y < 2100 && m >= 1 && m <= 12 && d >= 1 && d <= 31)) return "";
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}
const DATE_SRC = String.raw`\d{4}[-/.]\d{1,2}[-/.]\d{1,2}|\d{1,2}[\/.-]\d{1,2}[\/.-]\d{2,4}|\d{1,2}(?:st|nd|rd|th)?[\s\-.]+(?:of\s+)?[A-Za-z]{3,9}\.?,?[\s\-.]+\d{2,4}|[A-Za-z]{3,9}\.?[\s\-.]+\d{1,2}(?:st|nd|rd|th)?,?[\s\-.]+\d{4}`;
const DATE_RE = new RegExp(DATE_SRC, "i");

export function detectCurrency(text) {
  const counts = new Map();
  const bump = (c, n) => n && counts.set(c, (counts.get(c) || 0) + n);
  for (const c of CODES) bump(c, (text.match(new RegExp(`\\b${c}\\b`, "g")) || []).length * 2);
  for (const [c, re] of SYMBOLS) bump(c, (text.match(re) || []).length);
  const usd = (text.match(/(?<![A-Z])\$/g) || []).length - (text.match(/(A|C|S|NZ|HK|R|MX)\$/g) || []).length;
  bump("USD", usd);
  const best = [...counts.entries()].sort((a, b) => b[1] - a[1])[0];
  // "$1,130.00 CAD": a dollar sign plus an explicit code means that code
  const dollarCode = ["CAD", "AUD", "SGD", "NZD", "HKD"].find((c) => new RegExp(`\\b${c}\\b`).test(text));
  if (best?.[0] === "USD" && dollarCode && !/\bUSD\b/.test(text)) return dollarCode;
  if (!best && /GSTIN|\bRupees\b|\bRs\.?\s?\d/i.test(text)) return "INR";
  return best ? best[0] : "";
}

const snapRate = (r) => {
  if (!Number.isFinite(r) || r <= 0) return 0;
  const half = Math.round(r * 2) / 2;
  return Math.abs(half - r) < 0.06 ? half : Math.round(r * 100) / 100;
};

// ---------------- PDF → rows ----------------

let pdfjsPromise;
// The pdf.js worker is bundled into the page (globalThis.pdfjsWorker) instead of
// being fetched as a separate .mjs file — static hosts often serve .mjs with the
// wrong MIME type, which made PDF import fail with "Setting up fake worker failed".
export async function getPdfjs() {
  pdfjsPromise ||= (async () => {
    if (typeof window !== "undefined" && !globalThis.pdfjsWorker?.WorkerMessageHandler) {
      const worker = await import("pdfjs-dist/legacy/build/pdf.worker.min.mjs");
      if (!globalThis.pdfjsWorker?.WorkerMessageHandler && worker?.WorkerMessageHandler) {
        globalThis.pdfjsWorker = { WorkerMessageHandler: worker.WorkerMessageHandler };
      }
    }
    return import("pdfjs-dist/legacy/build/pdf.min.mjs");
  })().catch((e) => {
    pdfjsPromise = null;
    throw e;
  });
  return pdfjsPromise;
}

// Group positioned words into rows (top→bottom) of cells (left→right).
// items: [{ str, x, y (top-down), w, h }]
export function itemsToRows(items, { page = 1, width = 600, joinGap, wordGap } = {}) {
  const words = items.filter((it) => it.str && it.str.trim()).sort((a, b) => a.y - b.y || a.x - b.x);
  const lines = [];
  for (const it of words) {
    const tol = Math.max(2.5, (it.h || 10) * 0.45);
    let line = lines.find((l) => Math.abs(l.y - it.y) < tol);
    if (!line) lines.push((line = { y: it.y, h: it.h || 10, items: [] }));
    line.items.push(it);
  }
  lines.sort((a, b) => a.y - b.y);
  return lines.map((l) => {
    l.items.sort((a, b) => a.x - b.x);
    const cells = [];
    for (const it of l.items) {
      const last = cells[cells.length - 1];
      const gap = joinGap ?? Math.max(1.2, (it.h || l.h) * 0.12);
      if (last && it.x - (last.x + last.w) < gap) {
        last.str += it.str;
        last.w = it.x + it.w - last.x;
      } else if (last && it.x - (last.x + last.w) < (it.h || l.h) * (wordGap ?? 0.45) && !isMoney(last.str) && !isMoney(it.str) && /[A-Za-z]/.test(last.str + it.str) && !/[:]$/.test(last.str)) {
        // a normal word space inside one phrase
        last.str += " " + it.str;
        last.w = it.x + it.w - last.x;
      } else cells.push({ str: it.str, x: it.x, w: it.w });
    }
    return { page, width, y: l.y, h: l.h, cells: cells.map((c) => ({ ...c, str: c.str.replace(/\s+/g, " ").trim() })).filter((c) => c.str) };
  });
}

async function pdfPageRows(page, p) {
  const vp = page.getViewport({ scale: 1 });
  const { items } = await page.getTextContent();
  const words = items
    .filter((it) => it.str && it.str.trim())
    .map((it) => {
      const h = Math.abs(it.transform[3]) || Math.abs(it.transform[0]) || it.height || 10;
      return { str: it.str, x: it.transform[4], y: vp.height - it.transform[5], w: it.width || it.str.length * h * 0.5, h };
    });
  return itemsToRows(words, { page: p, width: vp.width });
}

// Returns rows (per page, top→bottom) of cells (left→right) with x positions.
export async function pdfRows(data, pdfjsLib) {
  const pdfjs = pdfjsLib || (await getPdfjs());
  const doc = await pdfjs.getDocument({ data: new Uint8Array(data), isEvalSupported: false }).promise;
  const rows = [];
  for (let p = 1; p <= doc.numPages; p++) rows.push(...(await pdfPageRows(await doc.getPage(p), p)));
  return { rows, doc };
}

// ---------------- OCR for scanned PDFs & photos ----------------

// The engine and English data are self-hosted in /ocr (see scripts/vendor-ocr.mjs)
// and only downloaded the first time a scan is imported.
let ocrPromise;
function loadTesseract() {
  if (typeof window === "undefined") return Promise.reject(new Error("OCR needs a browser"));
  if (window.Tesseract) return Promise.resolve(window.Tesseract);
  ocrPromise ||= new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `${basePath()}/ocr/tesseract.min.js`;
    s.onload = () => (window.Tesseract ? resolve(window.Tesseract) : reject(new Error("OCR engine failed to load")));
    s.onerror = () => {
      ocrPromise = null;
      reject(new Error("Couldn't load the OCR engine for scanned invoices"));
    };
    document.head.appendChild(s);
  });
  return ocrPromise;
}

async function ocrCanvas(canvas, page, onProgress) {
  const T = await loadTesseract();
  const base = basePath();
  const worker = await T.createWorker("eng", 1, {
    workerPath: `${base}/ocr/worker.min.js`,
    corePath: `${base}/ocr`,
    langPath: `${base}/ocr`,
    gzip: true,
    logger: (m) => m.status === "recognizing text" && onProgress?.(m.progress),
  });
  const scale = 2; // coordinates are only compared with each other
  const read = async (psm) => {
    await worker.setParameters({ preserve_interword_spaces: "1", tessedit_pageseg_mode: psm });
    const { data } = await worker.recognize(canvas);
    const words = (data.words || []).filter((w) => w.confidence > 30).map((w) => ({ str: w.text, x: w.bbox.x0 / scale, y: w.bbox.y0 / scale, w: (w.bbox.x1 - w.bbox.x0) / scale, h: (w.bbox.y1 - w.bbox.y0) / scale }));
    return itemsToRows(words, { page, width: canvas.width / scale, wordGap: 0.75 });
  };
  try {
    // automatic layout first; tables with ruled lines sometimes need "sparse text" mode
    let rows = await read("3");
    if (!findHeaders(rows).length) {
      const sparse = await read("11");
      if (findHeaders(sparse).length) rows = sparse;
    }
    return rows;
  } finally {
    await worker.terminate();
  }
}

async function ocrPdf(doc, onProgress) {
  const rows = [];
  for (let p = 1; p <= Math.min(doc.numPages, 5); p++) {
    const page = await doc.getPage(p);
    const vp = page.getViewport({ scale: 2 });
    const canvas = document.createElement("canvas");
    canvas.width = vp.width;
    canvas.height = vp.height;
    await page.render({ canvasContext: canvas.getContext("2d"), viewport: vp, canvas }).promise;
    rows.push(...(await ocrCanvas(canvas, p, (x) => onProgress?.(`Reading scanned page ${p}… ${Math.round(x * 100)}%`))));
  }
  return rows;
}

async function ocrImage(file, onProgress) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = () => rej(new Error("Not a readable image"));
      i.src = url;
    });
    const scale = Math.min(3, Math.max(0.5, 2400 / Math.max(img.width, img.height))) || 1;
    const canvas = document.createElement("canvas");
    canvas.width = img.width * scale;
    canvas.height = img.height * scale;
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    return ocrCanvas(canvas, 1, (x) => onProgress?.(`Reading image… ${Math.round(x * 100)}%`));
  } finally {
    URL.revokeObjectURL(url);
  }
}

// ---------------- invoice document parser ----------------

const rowText = (r) => r.cells.map((c) => c.str).join(" ");

// Column heading → field. Order matters (first match wins).
const COLS = [
  ["sr", /^(s\.?\s*no\.?|s\.?\s*r\.?\s*(no\.?)?|sl\.?\s*(no\.?)?|#|no\.?|item\s*(no\.?|#)|line)$/i],
  ["hsn", /^(hsn|sac)\b|^hsn\s*\/\s*sac|^(hsn|sac)\s*code/i],
  ["qty", /^(qty|quantity|qnty|hrs?|hours?|units?|nos?\.?|days?|months?|pcs|count|time|duration)\b(?!\s*(price|cost|rate))/i],
  ["disc", /^disc(ount)?\b/i],
  ["taxRate", /^(gst|tax|vat|igst|cgst|sgst)\s*(%|rate|\(%\))|^%$|^(tax|vat|gst)\s*\(%\)$|^rate\s*%$|^tax\s*rate/i],
  ["tax", /^(cgst|sgst|utgst|igst|tax|vat|gst|sales\s*tax)(\s*(amt\.?|amount|value))?(\s*\(.*\))?$/i],
  ["taxable", /^taxable(\s*(value|amount|amt\.?))?|^net\s*(value|amount|price)$|^amount\s*(before|excl)/i],
  ["rate", /^(unit\s*(price|cost|rate|amount)|rate|price|unit\s*cost|cost|fee|per\s*unit|each|rate\s*\/\s*unit|hourly\s*rate|price\s*per|rate\s*\(.*\)|price\s*\(.*\)|unit)/i],
  ["amount", /^(amount|total|line\s*total|value|amt\.?|sub\s*total|total\s*amount|gross|net|amount\s*\(.*\)|total\s*\(.*\)|price\s*total|total\s*price)/i],
  ["desc", /(description|particulars|item|service|product|details|work|task|deliverable|project|activity|^name$|campaign|package)/i],
];
const colType = (s) => {
  const t = String(s).trim();
  if (!t || t.length > 34 || /\d{2,}/.test(t.replace(/\(.*\)/, ""))) return "";
  return COLS.find(([, re]) => re.test(t))?.[0] || "";
};

// Find the item-table heading row(s).
function findHeaders(rows) {
  const found = [];
  for (let i = 0; i < rows.length; i++) {
    const cells = rows[i].cells;
    if (cells.length < 2 || cells.some((c) => isMoney(c.str))) continue;
    const types = cells.map((c) => colType(c.str));
    const set = new Set(types.filter(Boolean));
    const score = set.size;
    const ok = score >= 2 && (set.has("desc") || set.has("amount") || set.has("rate")) && (set.has("amount") || set.has("rate") || set.has("qty") || set.has("taxable"));
    if (!ok || types.filter(Boolean).length < cells.length * 0.5) continue;
    let cols = cells.map((c, k) => ({ type: types[k], x: c.x, w: c.w, label: c.str })).filter((c) => c.type);
    // two-line headings: "CGST" over "Rate | Amount", or "Unit" over "Price"
    const next = rows[i + 1];
    let sub = 0;
    // a heading word printed on its own line just above (e.g. "TAX" over "(0%)")
    for (const near of [rows[i - 1], rows[i + 1]]) {
      if (!near || near.page !== rows[i].page || Math.abs(near.y - rows[i].y) > rows[i].h * 1.3) continue;
      for (const c of near.cells) {
        const t = colType(c.str);
        if (t && !cols.some((p) => c.x + c.w > p.x && c.x < p.x + p.w)) cols.push({ type: t, x: c.x, w: c.w, label: c.str });
      }
      if (near === rows[i + 1] && near.cells.every((c) => !/\d/.test(c.str.replace(/\(\d+(\.\d+)?%\)/, "")))) sub = 1;
    }
    if (!sub && next && next.page === rows[i].page && next.y - rows[i].y < rows[i].h * 2.8 && !next.cells.some((c) => /\d/.test(c.str))) {
      for (const c of next.cells) {
        const cc = c.x + c.w / 2;
        const parent =
          cols.find((p) => c.x + c.w > p.x - 4 && c.x < p.x + p.w + 4) ||
          cols.filter((p) => /^(cgst|sgst|utgst|igst|tax|vat|gst)$/i.test(p.label) && Math.abs(p.x + p.w / 2 - cc) < 70).sort((a, b) => Math.abs(a.x + a.w / 2 - cc) - Math.abs(b.x + b.w / 2 - cc))[0];
        if (parent && /^(cgst|sgst|utgst|igst|tax|vat|gst)$/i.test(parent.label)) {
          cols.push({ type: /%|rate/i.test(c.str) ? "taxRate" : "tax", x: c.x, w: c.w, label: parent.label + " " + c.str });
        } else if (parent) {
          const t = colType(parent.label + " " + c.str);
          if (t) parent.type = t;
          parent.label += " " + c.str;
        } else {
          const t = colType(c.str);
          if (t) cols.push({ type: t, x: c.x, w: c.w, label: c.str });
        }
      }
      sub = 1;
      cols = cols.filter((c) => !(/^(cgst|sgst|utgst|igst)$/i.test(c.label) && cols.some((d) => d !== c && d.label.startsWith(c.label + " "))));
    }
    cols.sort((a, b) => a.x - b.x);
    // several "amount"-like columns: the right-most is the line total
    const amounts = cols.filter((c) => c.type === "amount");
    if (amounts.length > 1) amounts.slice(0, -1).forEach((c) => (c.type = cols.some((d) => d.type === "rate") ? "taxable" : "rate"));
    if (!cols.some((c) => c.type === "desc")) {
      const firstNum = cols.find((c) => c.type !== "sr");
      if (firstNum && firstNum.x > rows[i].width * 0.3) cols.unshift({ type: "desc", x: cols[0].type === "sr" ? cols[0].x + cols[0].w + 4 : 0, w: 60, label: "(description)" });
    }
    found.push({ index: i, page: rows[i].page, cols, sub });
    i += sub;
  }
  return found;
}

function assign(cell, cols) {
  const l = cell.x;
  const r = cell.x + cell.w;
  let best = null;
  let bestOverlap = 0;
  for (const c of cols) {
    const ov = Math.min(r, c.x + c.w) - Math.max(l, c.x);
    if (ov > bestOverlap) {
      bestOverlap = ov;
      best = c;
    }
  }
  if (best) return best;
  let bestD = Infinity;
  for (const c of cols) {
    const d = Math.max(c.x - r, l - (c.x + c.w), 0);
    if (d < bestD) {
      bestD = d;
      best = c;
    }
  }
  return best;
}

const STOP_RE = /^(sub\s*-?\s*total|total\b|grand\s*total|amount\s*(due|payable|in\s*words|chargeable)|balance(\s*due)?|total\s*due|(output\s*)?(vat|tax|gst|cgst|sgst|igst|sales\s*tax)\b(?!\s*invoice)|discount|shipping|less\b|in\s*words|rupees|payment\s*(terms|details|info|instructions)|bank\s*(details|name|account)|notes?\s*:?$|terms|thank|declaration|authori[sz]ed|for\s+[A-Z]|round(ing|\s*off))/i;
const isTotalLabel = (s) => {
  const lab = String(s).toLowerCase().replace(/\s+/g, " ").replace(/[:\-]\s*$/, "").replace(/\s*@?\s*\(?\d+(\.\d+)?\s*%\)?/, "").trim();
  return !!lab && !/\d{3}/.test(lab) && TOTAL_LABELS.some(([, re]) => re.test(lab));
};
const PAGE_CELL_RE = /\bpage\s*\d+\s*(of|\/)\s*\d+\b|^page\s*\d+$/i;
// A column value: "£1,200.00", "Rs. 500", or "GBP 12.00" – but not "Page 2 of 3".
function cellNumber(s) {
  if (s == null) return null;
  const t = String(s).trim();
  const whole = parseAmount(t);
  if (whole != null) return whole;
  const parts = t.split(/\s+/);
  const last = parseAmount(parts[parts.length - 1]);
  if (last == null) return null;
  return parts.slice(0, -1).every((p) => CUR_TOKEN.test(p) || isMoney(p) || parseAmount(p) != null) ? last : null;
}
const PAGE_RE = /^(page\s*\d+(\s*(of|\/)\s*\d+)?|continued|contd\.?|\d+\s*\/\s*\d+)$/i;

// Item rows from a detected table.
function tableItems(rows, headers) {
  const items = [];
  const used = new Set();
  for (let h = 0; h < headers.length; h++) {
    const { index, cols, sub } = headers[h];
    const end = headers[h + 1]?.index ?? rows.length;
    const block = [];
    for (let i = index + 1 + sub; i < end; i++) {
      const r = rows[i];
      const t = rowText(r);
      if (PAGE_RE.test(t)) continue;
      const first = r.cells[0]?.str || "";
      const descX = cols.find((x) => x.type === "desc");
      const hasDesc = r.cells.some((c) => descX && c.x < descX.x + descX.w + 40 && c.x + c.w > descX.x - 4 && !isMoney(c.str) && !isTotalLabel(c.str));
      if (STOP_RE.test(first) || (!hasDesc && r.cells.some((c) => isTotalLabel(c.str)))) {
        // totals reached – unless another heading follows on a later page
        if (headers[h + 1] && headers[h + 1].page > r.page) {
          i = end;
          continue;
        }
        break;
      }
      const vals = {};
      const descCol = cols.find((x) => x.type === "desc");
      for (const c of r.cells.filter((x) => !PAGE_CELL_RE.test(x.str))) {
        const col = assign(c, cols);
        if (!col) continue;
        if (col.type === "desc" && /^\d{1,3}\.?$/.test(c.str) && descCol && c.x + c.w <= descCol.x + 2) continue; // serial number
        vals[col.type] = vals[col.type] != null && col.type !== "tax" ? vals[col.type] + " " + c.str : col.type === "tax" && vals.tax != null ? vals.tax + "|" + c.str : c.str;
      }
      block.push({ i, r, vals });
    }
    // rows with numbers are items; text-only rows belong to the nearest item
    const numeric = (v) => ["amount", "rate", "taxable"].some((k) => v[k] != null && cellNumber(v[k]) != null);
    const isItem = block.map((b) => numeric(b.vals) && !(b.vals.desc && STOP_RE.test(b.vals.desc) && !b.vals.qty));
    const made = [];
    block.forEach((b, k) => {
      if (!isItem[k]) return;
      const v = b.vals;
      const lastNum = cellNumber;
      const qtyRaw = String(v.qty ?? "").match(/-?\d[\d,]*(\.\d+)?/);
      let qty = qtyRaw ? num(qtyRaw[0]) : 0;
      let rate = lastNum(v.rate);
      let amount = lastNum(v.amount);
      const taxable = lastNum(v.taxable);
      const tax = v.tax != null ? String(v.tax).split("|").reduce((s, x) => s + (lastNum(x) || 0), 0) : 0;
      let taxRate = v.taxRate != null ? String(v.taxRate).split(/\s+/).reduce((s, x) => s + (parseAmount(x) || 0), 0) : null;
      const disc = lastNum(v.disc) || 0;
      const qtyGiven = !!qty;
      if (!qty) qty = 1;
      let net = null;
      if (taxable != null) net = taxable;
      else if (amount != null) {
        if (rate != null && Math.abs(rate * qty - disc - amount) < 0.02) net = amount;
        else if (rate != null && tax && Math.abs(rate * qty - disc + tax - amount) < 0.02) net = round2(amount - tax); // amount includes tax
        else if (rate != null && !qtyGiven && rate > 0 && amount / rate > 1 && Math.abs(amount / rate - Math.round(amount / rate)) < 0.001) {
          qty = Math.round(amount / rate); // quantity column missing or unreadable
          net = amount;
        } else if (rate != null && Math.abs(rate - amount) < 0.01) net = amount; // "qty" that isn't a multiplier (e.g. hours shown for info)
        else net = amount; // trust the line total
      } else if (rate != null) net = round2(rate * qty - disc);
      if (net == null) net = 0;
      if (rate == null || Math.abs(rate * qty - disc - net) > 0.02) rate = round2((net + disc) / qty);
      if (taxRate == null && tax && net) taxRate = snapRate((tax / net) * 100);
      const unitRate = disc ? round2(net / qty) : rate;
      made.push({ k, y: b.r.y, page: b.r.page, item: { description: String(v.desc || "").trim(), hsn: String(v.hsn || "").match(/\d{4,8}/)?.[0] || "", qty, rate: unitRate ?? 0, tax, taxRate: taxRate ?? 0, amount: round2(net), gross: amount ?? round2(net + tax) } });
      used.add(b.i);
    });
    block.forEach((b, k) => {
      if (isItem[k] || !b.vals.desc || made.length === 0) return;
      const text = String(b.vals.desc).trim();
      if (!text || PAGE_RE.test(text)) return;
      const cands = made.filter((m) => m.page === b.r.page);
      if (!cands.length) return;
      const near = cands.reduce((a, m) => (Math.abs(m.y - b.r.y) < Math.abs(a.y - b.r.y) ? m : a));
      if (Math.abs(near.y - b.r.y) > (b.r.h || 10) * 3.2) return;
      const it = near.item;
      if (b.r.y < near.y) it.description = (text + " " + it.description).trim();
      else it.description = (it.description + " " + text).trim();
      used.add(b.i);
    });
    items.push(...made.map((m) => m.item));
  }
  return { items, used };
}

// Legacy layout: rows starting with a serial number followed by money values.
function serialItems(rows) {
  const items = [];
  const used = new Set();
  let last = null;
  rows.forEach((r, i) => {
    const cells = r.cells;
    const t = rowText(r);
    if (STOP_RE.test(cells[0]?.str || "")) last = null;
    const sr = /^\d{1,3}\.?$/.test(cells[0]?.str || "");
    const moneyIdx = cells.map((c, k) => (isMoney(c.str) ? k : -1)).filter((k) => k >= 0);
    if (sr && moneyIdx.length >= 1 && moneyIdx[0] > 1) {
      const firstMoney = moneyIdx[0];
      let qtyIdx = firstMoney - 1;
      let qty = 1;
      if (qtyIdx > 1 && /^\d+(\.\d+)?$/.test(cells[qtyIdx].str)) qty = num(cells[qtyIdx].str);
      else qtyIdx = firstMoney;
      let hsn = "";
      let descEnd = qtyIdx;
      if (descEnd - 1 > 1 && /^\d{4,8}$/.test(cells[descEnd - 1].str)) {
        hsn = cells[descEnd - 1].str;
        descEnd--;
      }
      const vals = moneyIdx.map((k) => money(cells[k].str));
      const amount = vals[vals.length - 1];
      const tax = vals.length >= 3 ? vals[vals.length - 2] : 0;
      last = { description: cells.slice(1, descEnd).map((c) => c.str).join(" "), hsn, qty, rate: vals.length >= 2 ? vals[0] : round2(amount / qty), tax, taxRate: 0, amount: vals.length >= 2 ? round2(vals[0] * qty) : amount };
      items.push(last);
      used.add(i);
    } else if (last && !sr && moneyIdx.length === 0 && cells.length <= 2 && cells[0].x > (r.width || 600) * 0.05 && !STOP_RE.test(t)) {
      last.description += " " + t;
      used.add(i);
    }
  });
  items.forEach((l) => (l.taxRate = l.tax && l.amount ? snapRate((l.tax / l.amount) * 100) : 0));
  return { items, used };
}

// Find the value for a label: same cell ("Invoice No: X"), the next cell on the
// row, or the cell right below it (card layouts where the label sits above the value).
function findLabeled(rows, labelRe, valueRe, { exclude } = {}) {
  const anchored = new RegExp(`^\\s*(?:${labelRe.source})\\s*[:#.\\-]?\\s*(.*)$`, "i");
  const clean = (v) => {
    const m = String(v || "").trim().match(valueRe);
    return m && !(exclude && exclude.test(m[0])) ? m[0] : "";
  };
  for (let i = 0; i < rows.length; i++) {
    const cells = rows[i].cells;
    for (let j = 0; j < cells.length; j++) {
      const m = cells[j].str.match(anchored);
      if (!m) continue;
      const v = clean(m[1]) || clean(cells[j + 1]?.str);
      if (v) return v;
      const below = rows[i + 1]?.cells.filter((c) => Math.abs(c.x - cells[j].x) < 30).sort((a, b) => Math.abs(a.x - cells[j].x) - Math.abs(b.x - cells[j].x))[0];
      if (below && rows[i + 1].y - rows[i].y < (rows[i].h || 10) * 3) {
        const bv = clean(below.str);
        if (bv) return bv;
      }
    }
  }
  // labels glued to the previous value ("AT/12Invoice Date: 2026-08-31")
  const loose = new RegExp(`(?:${labelRe.source.replace(/\^/g, "")})\\s*[:#.\\-]?\\s*`, "i");
  for (const r of rows) {
    const text = rowText(r);
    const m = text.match(loose);
    if (!m || /(due|payment)\s*$/i.test(text.slice(0, m.index))) continue;
    const v = clean(text.slice(m.index + m[0].length));
    if (v) return v;
  }
  return "";
}

const BILL_RE = /^(bill(ed)?\s*to|invoice(d)?\s*to|sold\s*to|customer(\s*(name|details|info))?|client(\s*(name|details|info))?|buyer(\s*(details|\(bill to\)))?|recipient|details\s*of\s*(receiver|recipient|buyer)|billing\s*(address|details|to)|party(\s*details)?|to|attention|prepared\s*for)\s*[:\-]?\s*/i;
const SECTION_RE = /^(ship(ped)?\s*to|deliver(y|ed)?\s*to|from|invoice\s*(no|number|#|date|details)|date|due|payment|bank|pay\s*to|remit|seller|supplier|place\s*of\s*supply|terms|notes?)\b/i;
const EMAIL_RE = /[\w.+-]+@[\w-]+(\.[\w-]+)+/;
const PHONE_RE = /(\+?\d[\d\s()-]{7,}\d)/;
const TAXID_RE = /\b(?:GSTIN|GST\s*(?:no|number|reg)|VAT\s*(?:no|number|reg(?:istration)?)?|TRN|ABN|EIN|Tax\s*ID|UEN|CIN|PAN)\b\s*[:#.\-]?\s*([A-Z0-9][A-Z0-9 -]{5,20}[A-Z0-9])/i;

function billToBlock(rows, stopIndex) {
  for (let i = 0; i < Math.min(rows.length, stopIndex); i++) {
    const cells = rows[i].cells;
    for (let j = 0; j < cells.length; j++) {
      const c = cells[j];
      const m = c.str.match(BILL_RE);
      if (!m || (/^(to|attention)/i.test(m[1]) && c.str.replace(BILL_RE, "").length > 0 && !/^to\s*:/i.test(c.str))) continue;
      if (/^to$/i.test(m[1]) && !/^to\s*:?$/i.test(c.str.trim())) continue;
      const lines = [];
      let inline = c.str.slice(m[0].length).replace(/^\(?\s*(bill(ed)?\s*to|buyer|customer|client)\s*\)?\s*:?/i, "").trim();
      let right = cells[j + 1];
      if (!inline && right && right.x - (c.x + c.w) < 40 && !SECTION_RE.test(right.str)) {
        inline = right.str;
        right = cells[j + 2];
      }
      const xmin = c.x - 6;
      const xmax = right ? right.x - 3 : rows[i].width + 1;
      if (inline) lines.push(inline);
      let lastY = rows[i].y;
      for (let k = i + 1; k < Math.min(rows.length, stopIndex, i + 10); k++) {
        const r = rows[k];
        if (r.page !== rows[i].page || r.y - lastY > (r.h || 10) * 2.6) break;
        // the block is the run of cells starting under the label (not a column further right)
        const part = [];
        for (const x of r.cells) {
          if (x.x < xmin || x.x >= xmax) continue;
          if (!part.length) {
            if (x.x > c.x + 80) break;
            part.push(x);
          } else if (x.x - (part[part.length - 1].x + part[part.length - 1].w) < 18) part.push(x);
          else break;
        }
        if (!part.length) continue;
        const t = part.map((x) => x.str).join(" ").trim();
        if (SECTION_RE.test(t) && lines.length) break;
        if (colType(part[0].str) && part.length > 1) break;
        lines.push(t);
        lastY = r.y;
      }
      if (lines.length) return lines;
    }
  }
  return [];
}

function parseBillTo(lines, full) {
  const out = {};
  const rest = [];
  for (let l of lines) {
    let m;
    if ((m = l.match(EMAIL_RE))) {
      out.email ||= m[0];
      l = l.replace(m[0], "").replace(/e-?mail\s*[:\-]?/i, "").trim();
    }
    if ((m = l.match(TAXID_RE))) {
      out.gstin ||= m[1].replace(/\s+/g, "");
      l = l.replace(m[0], "").trim();
    }
    if ((m = l.match(/(?:mobile|phone|tel|ph|contact|mob)\.?\s*(?:no\.?)?\s*[:\-]?\s*/i)) && PHONE_RE.test(l)) {
      out.phone ||= l.match(PHONE_RE)[1].trim();
      l = l.replace(PHONE_RE, "").replace(m[0], "").trim();
    } else if (/^\+?[\d\s()-]{9,}$/.test(l)) {
      out.phone ||= l.trim();
      l = "";
    }
    if ((m = l.match(/^(?:attn|attention|contact(?:\s*person)?|kind\s*attn)\.?\s*[:\-]?\s*(.+)/i))) {
      out.contactPerson = m[1].trim();
      l = "";
    }
    if ((m = l.match(/^(?:name|company|client|customer)\s*[:\-]\s*(.+)/i))) l = m[1];
    if ((m = l.match(/^address\s*[:\-]?\s*(.*)/i))) l = m[1];
    if ((m = l.match(/^(state|province|region|county)\s*[:\-]\s*(.+)/i))) {
      out.state = m[2].replace(/\s*\(?\b\d{2}\b\)?$/, "").replace(/[,;]\s*(code|state\s*code)\s*:?.*$/i, "").trim();
      l = "";
    }
    if ((m = l.match(/^(country)\s*[:\-]\s*(.+)/i))) {
      out.country = m[2].trim();
      l = "";
    }
    if ((m = l.match(/^(city|town)\s*[:\-]\s*(.+)/i))) {
      out.city = m[2].trim();
      l = "";
    }
    l = l.replace(/^[,;:\-\s]+|[,;:\-\s]+$/g, "");
    if (l) rest.push(l);
  }
  out.name = rest.shift() || "";
  const addr = [];
  for (const l of rest) if (!addr.some((a) => a.toLowerCase() === l.toLowerCase())) addr.push(l);
  if (addr.length) out.address = addr.join(", ");
  if (!out.gstin) {
    const gst = [...full.matchAll(/GSTIN\s*(?:\/\s*UIN)?\s*[:\-]?\s*([0-9]{2}[A-Z0-9]{13})/gi)].map((x) => x[1]);
    if (gst.length > 1) out.gstin = gst[1];
  }
  return out;
}

// Totals: pair each amount with the label to its left (skipping currency codes).
const TOTAL_LABELS = [
  ["subtotal", /^(sub\s*-?\s*total|taxable\s*(value|amount)|net\s*(amount|total)|amount\s*before\s*tax|total\s*(before|excl\.?|excluding)\s*(tax|vat|gst)?|total\s*ex\.?\s*(vat|gst|tax))/],
  ["cgst", /^cgst/],
  ["sgst", /^(sgst|utgst)/],
  ["igst", /^igst/],
  ["vat", /^((output\s*)?(vat|tax|sales\s*tax|gst|hst|pst|qst)|total\s*(vat|tax|gst))\b(?!\s*(invoice|no|number|reg|id|#))/],
  ["discount", /^(less\s*:?\s*)?discount/],
  ["shipping", /^(shipping|delivery|freight|postage|handling)/],
  ["roundoff", /^round(ing|ed)?\s*(off)?/],
  ["paid", /^(amount\s*paid|paid(\s*amount|\s*to\s*date)?|payment(s)?(\s*received|\s*made)?|received|less\s*:?\s*(paid|payment)|deposit\s*paid)/],
  ["balance", /^(balance(\s*due)?|amount\s*due|due\s*amount|outstanding|balance\s*payable|amount\s*outstanding)/],
  ["credit", /^(less\s*:?\s*)?(credit(?!\s*(note\s*)?total)(\s*(amount|applied|note))?|adjustment|advance(\s*received)?|retainer|tds)/],
  ["total", /^(total|grand\s*total|invoice\s*(total|amount|value)|amount\s*payable|net\s*payable|total\s*inc\w*|credit\s*(note\s*)?total)(\s*(amount|payable|due|invoice(\s*amount)?|value|(incl\.?|including)\s*(tax|vat|gst)?))?(\s*\(.*\)|\s*[a-z]{3}|\s*:)?$/],
];

function readTotals(rows, skip) {
  const t = { subtotal: null, cgst: 0, sgst: 0, igst: 0, vat: 0, taxLabel: "", discount: 0, shipping: 0, roundoff: 0, paid: null, balance: null, credit: 0, total: null };
  const seen = {};
  rows.forEach((r, i) => {
    if (skip.has(i)) return;
    const cells = r.cells;
    for (let k = 0; k < cells.length; k++) {
      let label = "";
      let value = null;
      const same = cells[k].str.match(/^([A-Za-z][A-Za-z .()&/%@\-:]*?)\s*[:\-]?\s*((?:[£₹$€¥]|[A-Z]{3}|Rs\.?)?\s*\(?-?[\d.,]+\)?(?:\s*[A-Z]{3})?)$/);
      if (same && isMoney(same[2]) && !colType(same[1]) && !CUR_TOKEN.test(same[1].trim()) && !CUR_TOKEN.test(same[1].trim().replace(/\.$/, "")) && /[a-z]{2}/i.test(same[1])) {
        label = same[1];
        value = money(same[2]);
      } else if (isMoney(cells[k].str) || (parseAmount(cells[k].str) != null && /^-?\d+$/.test(cells[k].str) && k > 0)) {
        value = money(cells[k].str);
        let li = k - 1;
        while (li >= 0 && (CUR_TOKEN.test(cells[li].str) || /^[\d.]+\s*%$|^\(?@?\s*[\d.]+\s*%\)?$/.test(cells[li].str) || (parseAmount(cells[li].str) != null && !isMoney(cells[li].str)))) li--;
        if (li < 0) continue;
        if (isMoney(cells[li].str)) continue;
        label = cells[li].str;
      } else continue;
      const lab = label.toLowerCase().replace(/\s+/g, " ").replace(/[:\-]\s*$/, "").trim();
      if (/words|qty|quantity|hours|items?$/.test(lab)) continue;
      const hit = TOTAL_LABELS.find(([, re]) => re.test(lab));
      if (!hit) continue;
      const key = hit[0];
      if (key === "vat") {
        t.vat += Math.abs(value);
        t.taxLabel ||= (label.match(/vat|sales\s*tax|gst|hst|pst|qst|tax/i)?.[0] || "Tax").replace(/^./, (x) => x.toUpperCase());
        if (/^(vat|gst|hst)$/i.test(t.taxLabel)) t.taxLabel = t.taxLabel.toUpperCase();
      } else if (["cgst", "sgst", "igst"].includes(key)) {
        if (!seen[key]) t[key] = Math.abs(value);
      } else if (key === "discount" || key === "credit" || key === "shipping") t[key] += Math.abs(value);
      else if (key === "roundoff") t.roundoff += value;
      else if (key === "total") t.total = Math.abs(value); // the last "total" is the grand total
      else if (key === "subtotal") t.subtotal ??= Math.abs(value);
      else t[key] = Math.abs(value);
      seen[key] = true;
    }
  });
  return t;
}

export function parseInvoiceRows(rows, { fileName = "" } = {}) {
  const full = rows.map(rowText).join("\n");
  const head = rows.slice(0, 25).map(rowText).join("\n");
  const out = { number: "", date: "", dueDate: "", currency: detectCurrency(full) || "", billTo: {}, items: [], cgst: 0, sgst: 0, igst: 0, vat: 0, taxLabel: "", credit: 0, discount: 0, total: 0, subtotal: 0, paid: 0, period: "", status: "", notes: "", warnings: [] };
  if (!rows.length) {
    out.warnings.push("No text found in this file.");
    return out;
  }
  const order = out.currency === "USD" || /\b(USA|United States)\b/i.test(full) ? "mdy" : "dmy";

  // ---- document type & fields ----
  out.docType = /credit\s*note/i.test(head) ? "credit" : /pro\s*-?\s*forma/i.test(head) ? "proforma" : /\b(quotation|quote|estimate)\b/i.test(head) && !/\binvoice\b/i.test(head) ? "quote" : "invoice";
  const NUM_RE = /[A-Z0-9][A-Z0-9\/\-_.]*\d[A-Z0-9\/\-_.]*/i;
  const numLabel = /(?:tax\s*)?(?:invoice|inv|bill|credit\s*note|debit\s*note|quote|quotation|estimate|receipt|document|doc|voucher|proforma(?:\s*invoice)?)\s*(?:no\.?|number|num|#|id|ref)|invoice\s*#|ref(?:erence)?\s*(?:no\.?|#)?|^no\./;
  out.number = findLabeled(rows, numLabel, NUM_RE, { exclude: new RegExp(`^(${DATE_SRC})$`, "i") }).replace(/(invoice|inv|bill|due|date|quote|credit|dated)$/i, "");
  if (!out.number) {
    const m = head.match(/\b(?:invoice|bill|receipt)\s*[#:]?\s*([A-Z]{0,6}[-/]?\d[A-Z0-9\/\-_.]{1,20})/i);
    if (m && !DATE_RE.test(m[1])) out.number = m[1];
  }
  out.number = out.number.replace(/[.,:;]+$/, "");
  out.date = parseDate(findLabeled(rows, /(?:tax\s*)?(?:invoice|inv|bill|issue|issued|document|doc|credit\s*note|quote)?\s*date|date\s*of\s*(?:issue|invoice)|dated|issued(?:\s*on)?|date\s*issued/, DATE_RE), order);
  out.dueDate = parseDate(findLabeled(rows, /(?:payment\s*)?due\s*(?:date|on|by)?|date\s*due|pay(?:ment)?\s*by/, DATE_RE), order);
  if (!out.date) {
    const m = head.match(new RegExp(`(?<!due\\s*(?:date)?\\s*[:\\-]?\\s*)(${DATE_SRC})`, "i"));
    out.date = m ? parseDate(m[1], order) : "";
  }
  if (!out.dueDate && out.date) {
    const net = full.match(/\bnet\s*(\d{1,3})\b|due\s*(?:with)?in\s*(\d{1,3})\s*days|(\d{1,3})\s*days\s*(?:credit|payment\s*terms)/i);
    const days = net && Number(net[1] || net[2] || net[3]);
    if (days) out.dueDate = new Date(new Date(out.date + "T00:00:00Z").getTime() + days * 864e5).toISOString().slice(0, 10);
    else if (/due\s*(on|upon)\s*receipt/i.test(full)) out.dueDate = out.date;
  }
  const per = full.match(/(?:billing|service|invoice)?\s*period\s*[:\-]?\s*([A-Za-z]{3,9}\.?\s*[-,]?\s*\d{4})|for\s*the\s*month\s*of\s*([A-Za-z]{3,9}\s*\d{4})/i);
  if (per) out.period = (per[1] || per[2]).trim();
  if (!out.currency) {
    const m = full.match(/currency\s*[:\-]?\s*([A-Z]{3})/i);
    if (m && CURRENCIES[m[1].toUpperCase()]) out.currency = m[1].toUpperCase();
  }

  // ---- items ----
  const headers = findHeaders(rows);
  let res = headers.length ? tableItems(rows, headers) : { items: [], used: new Set() };
  if (!res.items.length) res = serialItems(rows);
  out.items = res.items.filter((l) => l.description || l.amount);
  const skip = new Set([...res.used, ...headers.map((h) => h.index)]);

  // ---- bill to ----
  const stop = headers[0]?.index ?? rows.length;
  out.billTo = parseBillTo(billToBlock(rows, stop), full);

  // ---- totals ----
  const t = readTotals(rows, skip);
  Object.assign(out, { cgst: t.cgst, sgst: t.sgst, igst: t.igst, vat: t.vat, taxLabel: t.taxLabel, discount: t.discount, credit: t.credit, total: t.total ?? 0, subtotal: t.subtotal ?? 0, paid: t.paid ?? 0 });
  if (t.shipping) out.items.push({ description: "Shipping / delivery", hsn: "", qty: 1, rate: t.shipping, tax: 0, taxRate: 0, amount: t.shipping });
  if (t.cgst && !t.sgst && t.igst === 0 && t.vat === 0) out.sgst = t.cgst;
  if (out.cgst || out.sgst || out.igst) out.vat = 0;
  if (!out.total && t.balance != null && !t.paid) out.total = t.balance;

  // ---- reconcile ----
  let taxable = round2(out.items.reduce((s, l) => s + l.amount, 0));
  let tax = round2(out.cgst + out.sgst + out.igst + out.vat);
  const lineTax = round2(out.items.reduce((s, l) => s + (l.tax || 0), 0));
  if (!tax && lineTax) tax = lineTax;
  // the amount column already included tax
  if (tax && out.total && out.items.length && Math.abs(taxable - out.total) < 0.05 && Math.abs(taxable - tax - out.subtotal) < 0.05) {
    out.items.forEach((l) => (l.amount = round2(l.amount - (l.tax || (l.amount * tax) / taxable)), (l.rate = round2(l.amount / l.qty))));
    taxable = round2(taxable - tax);
  }
  // no item table found: keep the invoice value as one line so nothing is lost
  if (!out.items.length && (out.subtotal || out.total)) {
    const base = out.subtotal || round2(out.total - tax + out.discount + out.credit);
    out.items.push({ description: "", hsn: "", qty: 1, rate: base, tax, taxRate: 0, amount: base });
    taxable = base;
    out.warnings.push("Line items weren't listed in a table – imported the invoice total as one line.");
  }
  // tax not labelled but total = subtotal + something
  if (!tax && out.total && taxable && out.total - taxable + out.discount + out.credit > 0.05 && (!out.subtotal || Math.abs(out.subtotal - taxable) < 0.05)) {
    const diff = round2(out.total - taxable + out.discount + out.credit - t.roundoff);
    const r = (diff / taxable) * 100;
    if (r > 0.5 && r <= 30 && Math.abs(snapRate(r) - r) < 0.06) {
      tax = diff;
      out.vat = diff;
      out.taxLabel ||= out.currency === "INR" ? "GST" : "Tax";
    }
  }
  const rate = taxable && tax ? snapRate((tax / taxable) * 100) : 0;
  out.items = out.items.map((l) => ({ ...l, taxRate: tax ? l.taxRate || rate : 0 }));
  if (out.igst > 0) out.taxMode = "inter";
  else if (out.cgst > 0 || out.sgst > 0) out.taxMode = "intra";
  else if (tax > 0) out.taxMode = out.currency === "INR" && /gst/i.test(out.taxLabel) ? "intra" : "vat";
  else if (/\bLUT\b|export\s*of\s*services|zero[\s-]*rated|without\s*payment\s*of\s*(igst|tax)/i.test(full)) out.taxMode = "export";
  else out.taxMode = "";
  if (out.taxMode === "vat" && !out.taxLabel) out.taxLabel = "VAT";
  out.roundOff = t.roundoff && Math.abs(t.roundoff) < 1 ? round2(t.roundoff) : 0;

  // ---- status ----
  const computed = round2(taxable + tax - out.discount - out.credit + out.roundOff);
  const grand = out.total || computed;
  if (/\b(void|cancell?ed)\b/i.test(head)) out.status = "cancelled";
  else if (/^\s*paid\s*$/im.test(full) || /status\s*[:\-]?\s*paid\b/i.test(full) || /\bpaid\s*in\s*full\b/i.test(full) || (t.balance === 0 && grand > 0) || (t.paid && Math.abs(t.paid - grand) < 0.05)) out.status = "paid";
  else if (/status\s*[:\-]?\s*(unpaid|due|pending|overdue|outstanding)/i.test(full) || (t.paid && t.paid < grand)) out.status = "sent";
  else if (/\bdraft\b/i.test(head)) out.status = "draft";
  if (out.status === "paid") out.paid = grand;

  const n = full.match(/(?:^|\n)\s*(?:notes?|remarks?|memo)\s*[:\-]\s*([^\n]{3,200})/i);
  if (n) out.notes = n[1].trim();
  if (!out.number) {
    const m = String(fileName).match(/([A-Z]{0,6}[-_/]?\d[\w\-/]{1,20})/i);
    out.number = "";
    if (m) out.guessNumber = m[1];
  }
  if (!out.items.length) out.warnings.push("No line items or totals found.");
  if (out.total && Math.abs(computed - out.total) > 0.05) out.warnings.push(`Lines add up to ${computed.toFixed(2)} but the file's total is ${out.total.toFixed(2)}.`);
  out.computedTotal = computed;
  return out;
}

export async function parseInvoicePdf(data, pdfjsLib, opts = {}) {
  const { rows, doc } = await pdfRows(data, pdfjsLib);
  let r = rows;
  let ocr = false;
  if (rows.map(rowText).join("").replace(/\s/g, "").length < 20 && typeof window !== "undefined" && !pdfjsLib) {
    r = await ocrPdf(doc, opts.onProgress);
    ocr = true;
  }
  const out = parseInvoiceRows(r, opts);
  out.ocr = ocr;
  out.text = r.map(rowText).join("\n");
  if (ocr) out.warnings.push("Scanned document – read with OCR, please double-check the numbers.");
  return out;
}

export async function parseInvoiceImage(file, opts = {}) {
  const rows = await ocrImage(file, opts.onProgress);
  const out = parseInvoiceRows(rows, { fileName: file.name });
  out.ocr = true;
  out.text = rows.map(rowText).join("\n");
  out.warnings.push("Read from an image with OCR – please double-check the numbers.");
  return out;
}

// ---------------- CSV / Excel ----------------

export function parseCSV(text) {
  text = String(text).replace(/^﻿/, "");
  const firstLine = text.split(/\r?\n/)[0] || "";
  const delim = [",", ";", "\t", "|"].map((d) => [d, firstLine.split(d).length]).sort((a, b) => b[1] - a[1])[0][0];
  const rows = [];
  let row = [];
  let cell = "";
  let q = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (q) {
      if (ch === '"' && text[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') q = false;
      else cell += ch;
    } else if (ch === '"') q = true;
    else if (ch === delim) {
      row.push(cell);
      cell = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else cell += ch;
  }
  if (cell || row.length) rows.push([...row, cell]);
  return rows.filter((r) => r.some((c) => String(c).trim()));
}

const cellValue = (v) => {
  if (v == null) return "";
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === "object") {
    if (v.result !== undefined) return cellValue(v.result);
    if (v.richText) return v.richText.map((x) => x.text).join("");
    if (v.text) return v.text;
    if (v.error) return "";
    return "";
  }
  return v;
};

// All sheets of a workbook (or the single CSV) as grids of values.
export async function parseSpreadsheet(file) {
  if (/\.(csv|tsv|txt)$/i.test(file.name)) return [{ name: file.name, grid: parseCSV(await file.text()) }];
  if (/\.xls$/i.test(file.name)) throw new Error("Old .xls files aren't supported – open it in Excel and save as .xlsx or CSV");
  const { default: ExcelJS } = await import("exceljs");
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(await file.arrayBuffer());
  return wb.worksheets
    .filter((ws) => ws.state !== "hidden" && ws.state !== "veryHidden")
    .map((ws) => {
      const grid = [];
      ws.eachRow({ includeEmpty: true }, (r, n) => {
        grid[n - 1] = r.values.slice(1).map(cellValue);
      });
      for (let i = 0; i < grid.length; i++) grid[i] ||= [];
      return { name: ws.name, grid };
    })
    .filter((s) => s.grid.some((r) => r.some((c) => String(c).trim())));
}

// Register columns (one row per invoice or per line item).
const REG = [
  ["number", /^(tax\s*)?(invoice|inv|bill|document|doc|voucher|receipt)\s*(no\.?|number|num|#|id|ref(erence)?)$|^(invoice|inv\.?|bill)\s*#?$|^(no\.?|number|#|ref(erence)?(\s*no\.?)?)$/i],
  ["dueDate", /^(due(\s*date|\s*on|\s*by)?|date\s*due|payment\s*due(\s*date)?)$/i],
  ["date", /^((tax\s*)?(invoice|inv|bill|issue|document|doc|voucher|transaction|txn|posting)\s*)?date$|^date\s*(of\s*)?(issue|invoice)$|^issued(\s*(on|date))?$|^created(\s*(on|at|date))?$|^dated?$/i],
  ["client", /^(client|customer|party|buyer|company|account|bill(ed)?\s*to|sold\s*to|recipient|contact)(\s*(name|company))?$|^(name|client\s*\/\s*company)$/i],
  ["email", /e-?mail/i],
  ["phone", /^(phone|mobile|tel|contact\s*no)/i],
  ["gstin", /^(client\s*|customer\s*)?(gstin|gst\s*(no|number)|vat\s*(no|number|reg)|tax\s*id|trn|abn)/i],
  ["country", /^country$/i],
  ["project", /^project(\s*name)?$/i],
  ["description", /desc|^item(\s*name)?$|^service|particular|^product|details|memo|^line(\s*item)?$|narration/i],
  ["hsn", /^(hsn|sac)/i],
  ["qty", /^(qty|quantity|units?|hours?|hrs|nos?\.?|days?)$/i],
  ["rate", /^(rate|unit\s*(price|cost|rate)|price|unit)$/i],
  ["taxRate", /^((gst|tax|vat)\s*(%|rate|\(%\))|rate\s*%|%)$/i],
  ["cgst", /^cgst(\s*(amt\.?|amount))?$/i],
  ["sgst", /^(sgst|utgst)(\s*(amt\.?|amount))?$/i],
  ["igst", /^igst(\s*(amt\.?|amount))?$/i],
  ["tax", /^(tax|vat|gst|sales\s*tax|total\s*(tax|vat|gst))(\s*(amt\.?|amount))?(\s*\(.*\))?$/i],
  ["amount", /^((line\s*)?(amount|value)|taxable(\s*(value|amount))?|sub\s*-?total|net(\s*amount)?|amount\s*\(.*\)|amount\s*(excl|before|ex)\.?.*)$/i],
  ["total", /^(total|grand\s*total|invoice\s*(total|amount|value)|total\s*(amount|value|incl.*|\(.*\))|gross(\s*amount)?|amount\s*(incl|inc)\.?.*)$/i],
  ["paid", /^(paid|amount\s*paid|received|amount\s*received|payment(s)?(\s*received)?)$/i],
  ["balance", /^(balance(\s*due)?|amount\s*due|outstanding|due\s*amount)$/i],
  ["currency", /^(currency|curr\.?|ccy)$/i],
  ["status", /^(status|payment\s*status|state|paid\?)$/i],
  ["tag", /^(category|tag|type|service\s*type|department)$/i],
  ["discount", /^discount/i],
  ["notes", /^(notes?|remarks?|comments?)$/i],
];
function regType(h) {
  const t = String(h ?? "").trim();
  if (!t || t.length > 40) return "";
  return REG.find(([, re]) => re.test(t))?.[0] || "";
}

// Guess which columns hold what. Returns { headerRow, map } or null.
export function detectRegister(grid) {
  let best = null;
  for (let i = 0; i < Math.min(grid.length, 25); i++) {
    const map = {};
    grid[i].forEach((h, k) => {
      const t = regType(h);
      if (t && map[t] == null) map[t] = k;
    });
    const keys = Object.keys(map);
    const moneyCol = ["total", "amount", "rate", "balance"].some((k) => map[k] != null);
    const score = keys.length + (map.number != null ? 3 : 0) + (map.client != null ? 1 : 0) + (moneyCol ? 1 : 0);
    if (keys.length >= 2 && moneyCol && (map.number != null || map.client != null || map.date != null) && (!best || score > best.score)) best = { headerRow: i, map, score };
  }
  if (!best) return null;
  const data = grid.slice(best.headerRow + 1).filter((r) => r.some((c) => String(c).trim()));
  if (!data.length) return null;
  // the header row repeats "Total" etc. under an invoice layout – require real data rows
  const filled = data.filter((r) => ["total", "amount", "rate", "balance"].some((k) => best.map[k] != null && parseAmount(r[best.map[k]]) != null));
  return filled.length ? best : null;
}

// Group register rows into invoices.
export function rowsToDrafts(rows, map, defaults = {}) {
  const get = (r, k) => (map[k] != null && map[k] !== "" ? cellValue(r[map[k]]) : "");
  const str = (r, k) => String(get(r, k) ?? "").trim();
  const amt = (r, k) => parseAmount(get(r, k));
  const order = defaults.order || "dmy";
  const groups = new Map();
  let n = 0;
  for (const r of rows) {
    if (!r || !r.some((c) => String(cellValue(c)).trim())) continue;
    const first = String(cellValue(r.find((c) => String(cellValue(c)).trim())) || "");
    if (/^(sub\s*)?total|^grand\s*total/i.test(first) && map.number != null && !str(r, "number")) continue; // footer totals row
    let number = str(r, "number");
    if (/^(grand\s*)?(sub\s*-?\s*)?totals?\b/i.test(number) && !str(r, "date")) continue;
    if (map.number == null) number = `#${++n}`;
    if (!number) continue;
    const key = map.number == null ? number : `${number}|${str(r, "client")}`;
    if (!groups.has(key)) groups.set(key, { number: map.number == null ? "" : number, rows: [] });
    groups.get(key).rows.push(r);
  }
  return [...groups.values()].map(({ number, rows: rs }) => {
    const first = rs[0];
    const perInvoice = rs.length === 1;
    let items = rs.map((r) => {
      let qty = parseAmount(get(r, "qty"));
      const amount = amt(r, "amount");
      const total = amt(r, "total");
      const taxAmt = [amt(r, "cgst"), amt(r, "sgst"), amt(r, "igst"), amt(r, "tax")].reduce((s, v) => s + (v || 0), 0);
      let taxRate = parseAmount(get(r, "taxRate"));
      if (taxRate != null && taxRate > 0 && taxRate < 1) taxRate = round2(taxRate * 100); // 0.18 → 18%
      let rate = amt(r, "rate");
      if (!qty) qty = 1;
      let net = amount ?? (rate != null ? round2(rate * qty) : null);
      if (net == null && total != null && perInvoice) net = round2(total - taxAmt - (taxRate && !taxAmt ? (total * taxRate) / (100 + taxRate) : 0) + (amt(r, "discount") || 0));
      if (rate == null && net != null) rate = round2(net / qty);
      if (taxRate == null) taxRate = taxAmt && net ? snapRate((taxAmt / net) * 100) : 0;
      return { description: str(r, "description") || str(r, "project"), hsn: str(r, "hsn"), qty, rate: rate ?? 0, taxRate, tax: taxAmt, amount: net ?? 0, tag: str(r, "tag"), project: str(r, "project") };
    });
    items = items.filter((l) => l.rate || l.description);
    const total = rs.reduce((s, r) => Math.max(s, amt(r, "total") || 0), 0);
    const paidCol = rs.reduce((s, r) => Math.max(s, amt(r, "paid") || 0), 0);
    const balance = map.balance != null ? amt(first, "balance") : null;
    const statusRaw = str(first, "status").toLowerCase();
    const cgst = rs.reduce((s, r) => s + (amt(r, "cgst") || 0), 0);
    const sgst = rs.reduce((s, r) => s + (amt(r, "sgst") || 0), 0);
    const igst = rs.reduce((s, r) => s + (amt(r, "igst") || 0), 0);
    const anyTax = items.some((l) => l.taxRate);
    let currency = str(first, "currency").toUpperCase();
    if (!currency && (cgst || sgst || igst || /^\d{2}[A-Z]{5}\d{4}[A-Z]\w{3}$/.test(str(first, "gstin")))) currency = "INR";
    let status = "";
    if (statusRaw) {
      status = /draft/.test(statusRaw) ? "draft"
        : /cancel|void/.test(statusRaw) ? "cancelled"
        : /(^|\s)(paid|settled|received|closed|complete|yes|true)/.test(statusRaw) && !/un|not|part|pending|no\b/.test(statusRaw) ? "paid"
        : "sent";
    } else if (balance === 0 && total) status = "paid";
    else if (paidCol && total && paidCol >= total - 0.05) status = "paid";
    else if (paidCol || (balance && balance > 0)) status = "sent";
    return {
      number,
      date: parseDate(get(first, "date"), order),
      dueDate: parseDate(get(first, "dueDate"), order),
      currency: CURRENCIES[currency] ? currency : "",
      billTo: { name: str(first, "client"), email: str(first, "email"), phone: str(first, "phone"), gstin: str(first, "gstin"), country: str(first, "country") },
      items,
      total,
      paid: status === "paid" ? total : paidCol || (balance != null && total ? Math.max(0, total - balance) : 0),
      status,
      taxMode: igst ? "inter" : cgst || sgst ? "intra" : anyTax ? (currency === "INR" ? "intra" : "vat") : "",
      taxLabel: anyTax && currency !== "INR" ? "Tax" : "",
      credit: 0,
      discount: round2(rs.reduce((s, r) => s + (amt(r, "discount") || 0), 0)),
      notes: str(first, "notes"),
      warnings: [],
    };
  });
}

// Turn a grid into positioned rows so an invoice laid out in Excel can be read
// by the same parser as a PDF.
export function gridToRows(grid) {
  const text = (v) => {
    v = cellValue(v);
    if (typeof v === "number") return Number.isInteger(v) ? String(v) : v.toFixed(2);
    return String(v).replace(/\s+/g, " ").trim();
  };
  return grid
    .map((r, i) => ({
      page: 1,
      width: Math.max(1, r.length) * 100,
      y: i * 14,
      h: 10,
      cells: r.map((v, k) => ({ str: text(v), x: k * 100, w: 90 })).filter((c) => c.str),
    }))
    .filter((r) => r.cells.length);
}

// Read any spreadsheet automatically: registers are grouped into invoices,
// single-invoice layouts are parsed like a PDF.
export async function parseSpreadsheetFile(file, defaults = {}) {
  const sheets = await parseSpreadsheet(file);
  const drafts = [];
  for (const s of sheets) {
    const reg = detectRegister(s.grid);
    if (reg) {
      const sample = s.grid.slice(reg.headerRow + 1).map((r) => cellValue(r[reg.map.date])).join(" ");
      const order = /\b(0?[1-9]|1[0-2])\/(1[3-9]|2\d|3[01])\/\d{2,4}/.test(sample) ? "mdy" : defaults.order || "dmy";
      const got = rowsToDrafts(s.grid.slice(reg.headerRow + 1), reg.map, { ...defaults, order });
      got.forEach((d, k) => {
        d.source = sheets.length > 1 ? `${file.name} › ${s.name}` : file.name;
        if (!d.number) d.guessNumber = `${file.name.replace(/\.[^.]+$/, "")}-${k + 1}`;
      });
      drafts.push(...got);
    } else {
      const p = parseInvoiceRows(gridToRows(s.grid), { fileName: file.name });
      if (p.items.length || p.total || p.number) {
        p.source = sheets.length > 1 ? `${file.name} › ${s.name}` : file.name;
        drafts.push(p);
      }
    }
  }
  return drafts;
}
